<template>
  <v-card elevation="2">
    <v-toolbar color="deep-purple" dark flat>
      <v-icon left>mdi-calendar-clock</v-icon>
      <v-toolbar-title>Appointments</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="$emit('add-appointment')" v-if="canEdit">
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </v-toolbar>

    <v-tabs v-model="tab" background-color="deep-purple lighten-5">
      <v-tab>
        <v-icon left>mdi-calendar-today</v-icon>
        Upcoming
        <v-chip small class="ml-2" v-if="upcomingAppointments.length > 0">
          {{ upcomingAppointments.length }}
        </v-chip>
      </v-tab>
      <v-tab>
        <v-icon left>mdi-history</v-icon>
        Past
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <!-- Upcoming Appointments -->
      <v-tab-item>
        <v-card-text
          v-if="upcomingAppointments.length === 0"
          class="text-center py-8"
        >
          <v-icon size="64" color="grey lighten-1">mdi-calendar-blank</v-icon>
          <p class="text-h6 grey--text mt-4">No upcoming appointments</p>
          <v-btn
            color="deep-purple"
            dark
            @click="$emit('add-appointment')"
            v-if="canEdit"
          >
            <v-icon left>mdi-plus</v-icon>
            Schedule Appointment
          </v-btn>
        </v-card-text>

        <v-list v-else two-line>
          <template v-for="(appointment, index) in upcomingAppointments">
            <v-list-item :key="appointment.id">
              <v-list-item-avatar :color="getAppointmentColor(appointment)">
                <v-icon dark>mdi-calendar</v-icon>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">
                  {{ appointment.type }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <v-icon small>mdi-clock-outline</v-icon>
                  {{ formatDate(appointment.date) }} at {{ appointment.time }}
                </v-list-item-subtitle>
                <v-list-item-subtitle v-if="appointment.notes">
                  {{ appointment.notes }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action v-if="canEdit">
                <v-menu bottom left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item
                      @click="$emit('edit-appointment', appointment)"
                    >
                      <v-list-item-icon>
                        <v-icon>mdi-pencil</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Edit</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      @click="$emit('complete-appointment', appointment.id)"
                    >
                      <v-list-item-icon>
                        <v-icon color="green">mdi-check</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Mark Complete</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      @click="$emit('cancel-appointment', appointment.id)"
                    >
                      <v-list-item-icon>
                        <v-icon color="orange">mdi-cancel</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Cancel</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      @click="$emit('delete-appointment', appointment.id)"
                    >
                      <v-list-item-icon>
                        <v-icon color="red">mdi-delete</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Delete</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-list-item-action>
            </v-list-item>
            <v-divider
              :key="'div-' + index"
              v-if="index < upcomingAppointments.length - 1"
            ></v-divider>
          </template>
        </v-list>
      </v-tab-item>

      <!-- Past Appointments -->
      <v-tab-item>
        <v-card-text
          v-if="pastAppointments.length === 0"
          class="text-center py-8"
        >
          <v-icon size="64" color="grey lighten-1">mdi-history</v-icon>
          <p class="text-h6 grey--text mt-4">No past appointments</p>
        </v-card-text>

        <v-list v-else two-line>
          <template v-for="(appointment, index) in pastAppointments">
            <v-list-item :key="appointment.id">
              <v-list-item-avatar color="grey">
                <v-icon dark>mdi-calendar-check</v-icon>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title>
                  {{ appointment.type }}
                  <v-chip
                    x-small
                    :color="getStatusColor(appointment.status)"
                    dark
                    class="ml-2"
                  >
                    {{ appointment.status }}
                  </v-chip>
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ formatDate(appointment.date) }} at {{ appointment.time }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action>
                <v-btn
                  icon
                  small
                  @click="$emit('view-appointment', appointment)"
                >
                  <v-icon>mdi-eye</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
            <v-divider
              :key="'div-' + index"
              v-if="index < pastAppointments.length - 1"
            ></v-divider>
          </template>
        </v-list>
      </v-tab-item>
    </v-tabs-items>
  </v-card>
</template>

<script>
export default {
  name: "AppointmentsManager",

  props: {
    appointments: {
      type: Array,
      default: () => [],
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    tab: 0,
  }),

  computed: {
    upcomingAppointments() {
      const now = new Date();
      return this.appointments
        .filter((apt) => {
          const aptDate = new Date(apt.date);
          return aptDate >= now && apt.status !== "cancelled";
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    },

    pastAppointments() {
      const now = new Date();
      return this.appointments
        .filter((apt) => {
          const aptDate = new Date(apt.date);
          return aptDate < now;
        })
        .sort((a, b) => new Date(b.date) - new Date(a.date));
    },
  },

  methods: {
    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString();
    },

    getAppointmentColor(appointment) {
      if (appointment.status === "cancelled") return "red";

      const aptDate = new Date(appointment.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (aptDate.toDateString() === today.toDateString()) return "green";

      return "deep-purple";
    },

    getStatusColor(status) {
      const colors = {
        scheduled: "blue",
        completed: "green",
        cancelled: "red",
        "no-show": "orange",
      };

      return colors[status] || "grey";
    },
  },
};
</script>
